import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

// Define the context type
interface ClinicContextType {
  ClinicId: string;
  setClinicId: (id: string) => void;
}

// Create the ClinicContext
const ClinicContext = createContext<ClinicContextType | undefined>(undefined);

// Custom hook to use the ClinicContext
export const useClinic = (): ClinicContextType => {
  const context = useContext(ClinicContext);
  if (!context) {
    throw new Error('useClinic must be used within a ClinicProvider');
  }
  return context;
};

// ClinicProvider component to wrap the app
export const ClinicProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [ClinicId, setClinicIdState] = useState<string>(() => {
    // Initialize from localStorage if available
    return localStorage.getItem('clinicId') || '';
  });

  // Memoize setClinicId to avoid re-creation on each render
  const setClinicId = useCallback((id: string) => {
    setClinicIdState(id);
    // Store in localStorage so httpService can access it
    // Remove the key when id is falsy to avoid stale clinic data across sessions
    if (id) {
      localStorage.setItem('clinicId', id);
    } else {
      localStorage.removeItem('clinicId');
    }
  }, []);

  return (
    <ClinicContext.Provider value={{ ClinicId, setClinicId }}>
      {children}
    </ClinicContext.Provider>
  );
};